import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useRef } from "react";
import { Kachel } from "../design/Kachel";
import { Scrollbereich } from "../design/Scrollbereich";
import { Symbol } from "../design/Symbol";
import type { Zeile } from "../plattform/lesemodelle";
import { personAus, positionAus, positionText } from "./daten";
import { NOTFALLSYMBOL } from "./eingehend";

/**
 * Eingehender Notruf (oberflaeche.md §5; design/README §9.10, §14 0g F-g1, Vorschau `#s-notruf-ein`): `alertdialog`
 * „Notruf“ an Stelle des Lagebilds mit `article` „<Notfallart>“ – Zeilen „Begriff Wert“ in der Reihenfolge der Referenz:
 * Stand, Position des Melders mit Genauigkeit und Entfernung vom eigenen Gerät mit Richtung, Person, Rettungspunkt –,
 * dahinter im selben Scrollbereich „Navigation“ und „Anrufen“; „Quittieren“ als letztes Kind unten in Daumenreichweite.
 * Kein Notrufband (der Dialog ist der Notruf). Während der Eingabesperre (500 ms) ist der Dialog nicht bedienbar.
 */
export function NotrufEingang({ notruf, entfernung, rettungspunkt, gesperrt, onQuittieren, onNavigation, onAnrufen }: {
  notruf: Zeile;
  /** „1,2 km NO“; ohne eigene Position bzw. Position des Melders `null` */
  entfernung: string | null;
  /** „UE-201 · 230 m“ */
  rettungspunkt: string | null;
  gesperrt: boolean;
  onQuittieren: () => void;
  onNavigation?: () => void;
  onAnrufen?: () => void;
}) {
  const art = String(notruf["Notfallart"]);
  const titelId = `notein-${notruf._id}`;
  const position = positionAus(notruf["Position"]);
  const genauigkeit = typeof notruf["Genauigkeit"] === "number" ? notruf["Genauigkeit"] : null;
  const zeilen: Array<[string, string, string?]> = [
    ["Stand", String(notruf["Stand"] ?? "—")],
    ["Position", position ? [positionText(position), genauigkeit !== null ? `± ${genauigkeit} m` : null, entfernung].filter(Boolean).join(" · ") : "—", "lesen kennung"],
    ["Person", personAus(notruf["Melder"])?.Name ?? "—"],
    ["Rettungspunkt", rettungspunkt ?? "—", "lesen kennung"],
    ...(notruf["Hund"] ? [["Hund", String(notruf["Hund"])]] as Array<[string, string, string?]> : []),
    ...(notruf["Bemerkung"] ? [["Bemerkung", String(notruf["Bemerkung"])]] as Array<[string, string, string?]> : []),
  ];
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!gesperrt) dialog.current?.focus();
  }, [gesperrt, notruf._id]);
  return (
    <div ref={dialog} className="notein" role="alertdialog" aria-label="Notruf" aria-modal="true" tabIndex={-1} inert={gesperrt}>
      <article className="notein__art platte" aria-labelledby={titelId}>
        <h2 className="notein__t notruf-titel" id={titelId}><Symbol name={NOTFALLSYMBOL[art] ?? "notruf"} /><span>{art}</span></h2>
        <Scrollbereich>
          <table className="anrufzeilen notein__zeilen" role="table">
            <tbody>
              {zeilen.map(([feld, wert, klasse]) => (
                <tr key={feld} role="row"><th scope="row" role="rowheader">{feld}</th><td role="cell" className={klasse}>{wert}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="raster raster--2">
            <Kachel symbol="navigation" begriff="Navigation" art="kompakt" isDisabled={!position || !onNavigation} onPress={onNavigation} />
            <Kachel symbol="telefon" begriff="Anrufen" art="kompakt" isDisabled={!onAnrufen} onPress={onAnrufen} />
          </div>
        </Scrollbereich>
      </article>
      <Kachel symbol="quittieren" begriff="Quittieren" art="kern" className="k--quittieren" onPress={onQuittieren} />
    </div>
  );
}

/** Notrufband im Zustandsband (design/README §9.2): „Notruf · Stand 7a · 10:17“. */
export function Notrufband({ notruf }: { notruf: Zeile }) {
  const zeit = notruf["Zeitpunkt"] instanceof Date ? berlinUhrzeit(notruf["Zeitpunkt"]) : null;
  const text = ["Notruf", notruf["Stand"] ? `Stand ${String(notruf["Stand"])}` : null, zeit].filter(Boolean).join(" · ");
  return <span className="leiste notrufband" role="status"><Symbol name="notruf" /><span>{text}</span></span>;
}

/** Bestätigung nach „Quittieren“ über dem Lagebild (Vorschau `#s-quittiert`): Begriff, Notfallart, Stand und Uhrzeit. */
export function Quittiert({ notfallart, stand, zeit }: { notfallart: string; stand: string | null; zeit: Date }) {
  return (
    <div className="best best--leitung" role="status">
      <Symbol name="quittieren" className="best__haken" />
      <b className="best__b">Quittieren</b>
      <div className="best__angaben"><span className="best__a"><Symbol name={NOTFALLSYMBOL[notfallart] ?? "notruf"} /><span>{notfallart}</span></span></div>
      <span className="best__m">{[stand ? `Stand ${stand}` : null, berlinUhrzeit(zeit)].filter(Boolean).join(" · ")}</span>
    </div>
  );
}
